import { cloneDeep, omit, isEmpty } from 'lodash';
import { RowUpdateStatus } from '../../../helpers';
import { stringToHash } from '../../../helpers';

export class DataSource {
    _firstLoad = true;
    _refreshType = null;
    _reloadRows = null;
    gridApi = null;
    table = null;
    currentRowData = null;
    currentRowCount = 0;
    currentPageRowCount = 0;
    pageIndex = 0;
    pageSize = 20;
    filterModel = null;
    lastRequest = null;
    onLoadRows = null;
    processed = 0;
    // Ag-Grid notify a cell is being updated if at the end of a cell edit operation it finds a null or a string.empty value
    // To avoid this we need to ensure that each column value is translated to undefined if its value is null or string.empty
    ensureRowEmptyAsUndefined(rowData) {
        const keys = Object.keys(rowData);
        keys.forEach((key) => {
            rowData[key] = rowData[key] !== "" && rowData[key] !== null ? rowData[key] : undefined;
        });
        return rowData;
    }
    // Generate and attach a Universally Unique Identifier to a grid row
    // The UUID is generated reading all the PK fields of a row
    // This value is then used to identify the row serverside for updates or deletions
    ensureRowUUID(rowData) {
        if (rowData.uuid === undefined || (rowData.uuid && rowData.uuid.length === 0) || rowData.isNew) {
            const pkColumnNames = this.table.columns.filter(x => x.isPrimaryKey).map(x => x.name);
            const pkColumnValues = [];
            pkColumnNames.forEach(pkName => pkColumnValues.push((rowData[pkName] ?? "").toString()));
            const uuid = pkColumnValues.filter(x => x && x.length > 0).join(",");
            rowData["uuid"] = uuid;
        }
        return rowData;
    }
    // Generate an hash key based on row data
    // The key is then used to determine if an user has modified any value of the row
    // A getChangeHash() is also attached to the row data and will be used to calculate 
    // a new has using the latest values from the row
    ensureRowChangeHash(rowData) {
        rowData["changeHash"] = stringToHash(JSON.stringify(omit(rowData, ["changeHash", "getChangeHash"])));
        console.debug(`Row hash is ${rowData["changeHash"]}`);
        rowData["getChangeHash"] = () => {
            const rowHash = stringToHash(JSON.stringify(omit(rowData, ["changeHash", "getChangeHash"])));
            console.debug(`Row hash is ${rowHash}`);
            return rowHash;
        }
        return rowData;
    }
    addRow(params) {
        const newRows = cloneDeep(this.currentRowData);
        const newRow = cloneDeep(params.rowData);
        newRow.isNew = true;
        newRow.isChanged = true;
        newRows.push(newRow);
        this.currentRowData = newRows;
        this.currentRowCount = newRows.length;
        this._refreshType = "addNew";
        params.api.deselectAll();
        params.api.refreshServerSide({ purge: newRows.length === 1 ? true : false });
    }
    updateRow(params) {
        const newRows = cloneDeep(this.currentRowData);
        const oldRow = this.currentRowData[params.rowIndex];
        newRows[params.rowIndex] = { ...oldRow, ...params.rowData, isChanged: true, error: null };
        this.currentRowData = newRows;
        this._refreshType = "update";
        params.api.refreshServerSide({ purge: false });
    }
    updateRows(params) {
        const newRows = cloneDeep(this.currentRowData);
        params.rowUpdates.forEach((rowUpdate) => {
            const oldRow = this.currentRowData[rowUpdate.rowIndex];
            newRows[rowUpdate.rowIndex] = { ...oldRow, ...rowUpdate.rowData, isChanged: true, error: null };
        });
        this.currentRowData = newRows;
        this._refreshType = "update";
        params.api.refreshServerSide({ purge: false });
    }
    deleteRows(params) {
        const newRows = cloneDeep(this.currentRowData)
            .filter((x, i) => params.rowIndexes.find((rowIndex) => rowIndex === i) === undefined);
        this.currentRowData = newRows;
        this.currentRowCount = newRows.length;
        this._refreshType = "delete";
        params.api.deselectAll();
        params.api.refreshServerSide({ purge: false });
    }
    cancelChanges(params) {
        this._refreshType = null;
        params.api.deselectAll();
        params.api.refreshServerSide({ purge: true });
    }
    stopEditing(params) {
        params.api.deselectAll();
        params.api.stopEditing(params.cancel);
    }
    deselectAll(params) {
        params.api.deselectAll();
    }
    refreshRowsStatus(params) {
        const newRows = cloneDeep(this.currentRowData);
        params.rowsStatus.forEach((rowStatus) => {
            if (rowStatus.status === RowUpdateStatus.Ok) {
                delete newRows[rowStatus.rowIndex].changeId;
                newRows[rowStatus.rowIndex] = rowStatus.updatedData;
                newRows[rowStatus.rowIndex].isChanged = false;
                newRows[rowStatus.rowIndex].error = null;
                newRows[rowStatus.rowIndex].errorInfo = null;
            } else if (rowStatus.status === RowUpdateStatus.Error) {
                newRows[rowStatus.rowIndex].error = rowStatus.message;
                newRows[rowStatus.rowIndex].errorInfo = rowStatus.errorInfo;
            }
        })
        this.currentRowData = newRows;
        this._refreshType = "refresh";
        params.api.refreshServerSide({ purge: false });
    }
    reloadRows(params) {
        this.pageIndex = params.pageIndex ?? this.pageIndex;
        this.pageSize = params.pageSize ?? this.pageSize;
        this._refreshType = null;
        params.api.refreshServerSide({ purge: true });
    }
    getRows(params) {
        try {
            this._reloadRows = params.success;
            if (this._refreshType !== null) {
                params.success({
                    rowData: this.currentRowData.map(x =>
                        this.ensureRowChangeHash(this.ensureRowEmptyAsUndefined(this.ensureRowUUID(x)))),
                    rowCount: this.currentRowCount < this.pageSize ?
                        this.currentRowCount : this.currentPageRowCount + this.currentRowData.filter(x => x.isNew).length
                });
                if (this._refreshType === "addNew") {
                    params.api.startEditingCell({ rowIndex: this.currentRowCount - 1, colKey: this.table.columns.find(x => !x.isIdentity).name })
                }
            } else {
                if (this._firstLoad) {
                    this._firstLoad = false;
                    if (this.filterModel && !isEmpty(this.filterModel)) {
                        params.api.setFilterModel(this.filterModel);
                        // No need to call the onFilterChanged() method!
                        //params.api.onFilterChanged();
                        return;
                    }
                }
                params.request.startRow = (this.pageIndex * this.pageSize);
                params.request.endRow = (this.pageIndex * this.pageSize) + this.pageSize;
                this.lastRequest = params.request;
                const json = JSON.stringify(params.request, null, 1);
                console.debug(json);
                if (this.onLoadRows) {
                    const sortAndFilter = btoa(json);
                    this.onLoadRows(sortAndFilter, (rowData, rowCount) => {
                        this.currentRowData = rowData.map(x =>
                            this.ensureRowChangeHash(this.ensureRowEmptyAsUndefined(this.ensureRowUUID(x))));
                        this.currentRowCount = rowCount;
                        this.currentPageRowCount = rowData.length;
                        params.success({
                            rowData: this.currentRowData,
                            //rowCount: Math.min(this.pageSize, rowData.length)
                            rowCount: this.currentPageRowCount
                        });
                    });
                } else {
                    params.success({
                        rowData: [],
                        rowCount: 0
                    });
                }
            }
            this._refreshType = null;
        } catch (e) {
            console.debug(e);
            params.fail();
        }
    }
}